import { useState } from 'react';
import { EstimatedTag } from '../ui/EstimatedTag';
import { useQuery } from '@tanstack/react-query';
import type { MetricPoint } from '@lizrd/contracts';
import {
  getCostTrends,
  getDomainAssessment,
  getDomainHistory,
  getRecommendations,
} from '../../lib/api';
import { Card } from '../ui/card';
import { Select } from '../ui/select';
import { SkeletonText } from '../ui/skeleton';
import { LineChart } from '../ui/line-chart';
import { seriesDelta } from '../ui/trend-chart';
import { usd } from '../../lib/graph-visuals';
import { cumulativeRealized, periodSince } from '../../lib/savings';

type PeriodKey = '30' | '60' | '90' | 'all';
const PERIOD_OPTIONS = [
  { value: '30' as const, label: 'Last 30 days' },
  { value: '60' as const, label: 'Last 60 days' },
  { value: '90' as const, label: 'Last 90 days' },
  { value: 'all' as const, label: 'All time' },
];
const periodDays = (p: PeriodKey) => (p === 'all' ? null : Number(p));

/** A ▲/▼ delta caption for a series; lower cost is good, higher score is good. */
function DeltaCaption({
  delta,
  goodWhenDown = false,
  format,
}: {
  delta: { abs: number; pct: number | null } | null;
  goodWhenDown?: boolean;
  format: (n: number) => string;
}) {
  if (!delta || delta.abs === 0) {
    return <span className="text-[0.65rem] text-muted">No change yet</span>;
  }
  const down = delta.abs < 0;
  const good = goodWhenDown ? down : !down;
  return (
    <span className={`text-[0.65rem] font-semibold ${good ? 'text-success' : 'text-danger'}`}>
      {down ? '▼' : '▲'} {format(Math.abs(delta.abs))}
      {delta.pct != null && ` (${down ? '' : '+'}${delta.pct}%)`}
    </span>
  );
}

const withinSince = (points: MetricPoint[], since: Date | null) =>
  since ? points.filter((p) => new Date(p.day) >= since) : points;

/**
 * Domain trends (DASH-004/005): daily spend, Utilization Score (1–5), and
 * cumulative realized savings over a selectable window (30/60/90 days or all
 * time). The window drives every chart, so an engineer can see whether the
 * domain is getting cheaper, better-used, and banking savings over time.
 */
export function DomainTrends({ domainId }: { domainId: string }) {
  const [period, setPeriod] = useState<PeriodKey>('30');
  const days = periodDays(period);
  const since = periodSince(days);

  const costTrends = useQuery({
    queryKey: ['cost-trends', domainId, days ?? 'all'],
    queryFn: () => getCostTrends(domainId, days ?? 3650),
    refetchInterval: 60_000,
  });
  const history = useQuery({
    queryKey: ['domain-history', domainId],
    queryFn: () => getDomainHistory(domainId),
    refetchInterval: 60_000,
  });
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    refetchInterval: 60_000,
  });
  // Shares the header's query key, so this is deduped — no extra request.
  const assessment = useQuery({
    queryKey: ['domain-assessment', domainId],
    queryFn: () => getDomainAssessment(domainId),
    refetchInterval: 60_000,
  });

  // Cost line: when the domain has a real billed signal, show its PROJECTED
  // monthly cost — keep the estimated series' trend shape (how the footprint
  // moved) but scale it so the latest point lands on the billed full-month
  // projection (projectMonthlySpend, exposed as projectedBilledCost). Level =
  // billed reality, curve = trend. Fall back to the raw estimate (flagged) only
  // when there's no billed cost.
  const a = assessment.data;
  const hasBilled = a != null && a.billedMonthlyCost != null && a.billedMonthlyCost > 0;
  const projectedMonthly = hasBilled ? (a!.projectedBilledCost ?? a!.billedMonthlyCost!) : null;
  const rawCost = costTrends.data?.total ?? [];
  const latestEstimated = rawCost.length ? rawCost[rawCost.length - 1]!.value : 0;
  const costScale = hasBilled && latestEstimated > 0 ? projectedMonthly! / latestEstimated : 1;
  const costSeries =
    costScale === 1
      ? rawCost
      : rawCost.map((p) => ({ ...p, value: Math.round(p.value * costScale) }));
  const costLabel = hasBilled ? 'Projected monthly cost' : 'Daily cost';
  const scoreSeries = withinSince(history.data?.utilizationScore ?? [], since);
  const savingsSeries = recs.data ? cumulativeRealized(recs.data.items, since) : [];

  return (
    <Card className="space-y-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Trends
        </h2>
        <Select
          value={period}
          onChange={(v) => setPeriod(v as PeriodKey)}
          ariaLabel="Time window"
          size="sm"
          options={PERIOD_OPTIONS}
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span
            className="text-xs text-haze"
            title={
              hasBilled
                ? 'Your billed cost projected to a full month (run rate), shown along the estimated spend trend.'
                : undefined
            }
          >
            {costLabel} {!hasBilled && <EstimatedTag />}
          </span>
          <DeltaCaption delta={seriesDelta(costSeries)} goodWhenDown format={usd} />
        </div>
        {!costTrends.data ? (
          <SkeletonText lines={3} lineClassName="h-4" />
        ) : (
          <LineChart
            series={[{ key: 'cost', label: costLabel, color: 'var(--iris)', points: costSeries }]}
            formatValue={usd}
          />
        )}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs text-haze">Utilization score</span>
          <DeltaCaption delta={seriesDelta(scoreSeries)} format={(n) => n.toFixed(1)} />
        </div>
        {!history.data ? (
          <SkeletonText lines={3} lineClassName="h-4" />
        ) : (
          <LineChart
            series={[
              { key: 'score', label: 'Utilization', color: 'var(--success)', points: scoreSeries },
            ]}
            yMax={5}
            yTicks={[1, 2, 3, 4, 5]}
            formatValue={(n) => `${n.toFixed(1)}/5`}
            formatTick={(n) => `${n}`}
          />
        )}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-xs text-haze">Cost savings (validated, cumulative)</span>
          <span className="text-[0.65rem] text-muted">
            {savingsSeries.length > 0
              ? `${usd(savingsSeries[savingsSeries.length - 1]!.value)}/mo`
              : ''}
          </span>
        </div>
        {!recs.data ? (
          <SkeletonText lines={3} lineClassName="h-4" />
        ) : savingsSeries.length === 0 ? (
          <div className="grid h-[120px] place-items-center rounded-sm bg-surface-2/40 text-[0.7rem] text-muted">
            No validated savings in this window yet
          </div>
        ) : (
          <LineChart
            series={[
              { key: 'savings', label: 'Realized', color: 'var(--iris)', points: savingsSeries },
            ]}
            formatValue={(n) => `${usd(n)}/mo`}
          />
        )}
      </div>
    </Card>
  );
}
